import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { monitoringWebSocket } from '../../services/websocketService';
import { stopControlRoomSiren } from '../../services/controlRoomSirenService';
import { CONTROL_ROOM_SIREN_DIALOG_Z } from './centralRiskEventTypes';

type ControlRoomSirenPayload = {
  action?: string;
  alert_level?: string;
  message?: string;
  event?: {
    event_id?: string | number;
    pool_id?: number;
    pool_name?: string;
    event_type?: string;
    risk_level?: string;
  };
  reason?: string;
};

/**
 * AI/수동 이벤트 시 관제실 경광등 알림 — 「경광등 끄기」.
 * 위험 이벤트·알림 전송 팝업(CENTRAL_RISK_DIALOG_Z)보다 위에 띄워 관제사가 먼저 인지.
 * 시설 경광·수동 safety_alert 팝업과 분리.
 */
const ControlRoomSirenDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<ControlRoomSirenPayload | null>(null);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStop = useCallback(async () => {
    setStopping(true);
    setError(null);
    try {
      await stopControlRoomSiren();
      setOpen(false);
      setPayload(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '경광등 중지 요청 실패');
    } finally {
      setStopping(false);
    }
  }, []);

  useEffect(() => {
    monitoringWebSocket.enableWebSocket();
    monitoringWebSocket.connectToControlCenter();

    const onSiren = (data: unknown) => {
      const raw = data as ControlRoomSirenPayload & { data?: ControlRoomSirenPayload };
      if (!raw || typeof raw !== 'object') return;
      const body: ControlRoomSirenPayload =
        raw.data && typeof raw.data === 'object' ? raw.data : raw;
      const action = String(body.action || '').toLowerCase();
      if (action === 'stop') {
        setOpen(false);
        setPayload(null);
        setError(null);
        return;
      }
      if (action !== 'start') return;
      setPayload(body);
      setOpen(true);
      setError(null);
    };

    monitoringWebSocket.subscribe('control_room_siren', onSiren);
    return () => monitoringWebSocket.unsubscribe('control_room_siren', onSiren);
  }, []);

  const isDanger = String(payload?.alert_level || '').toLowerCase() === 'danger';
  const poolName = payload?.event?.pool_name || '';
  const risk = payload?.event?.risk_level || '';
  const eventType = payload?.event?.event_type || '';

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          border: isDanger ? '3px solid #c62828' : '3px solid #ef6c00',
          bgcolor: isDanger ? '#1a0505' : '#1a1208',
          color: '#fff',
        },
      }}
      sx={{ zIndex: CONTROL_ROOM_SIREN_DIALOG_Z }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#fff' }}>
        <CampaignIcon color={isDanger ? 'error' : 'warning'} fontSize="large" />
        관제실 경광등
        {poolName ? ` — ${poolName}` : null}
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {payload?.message || 'AI 감지 이벤트 — 관제실 경광등'}
        </Typography>
        {(risk || eventType) && (
          <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
            {[risk, eventType].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          경광등이 울리는 중입니다. 확인 후 「경광등 끄기」를 누르세요.
        </Typography>
        {error ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color={isDanger ? 'error' : 'warning'}
          size="large"
          disabled={stopping}
          onClick={() => void handleStop()}
          startIcon={stopping ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          경광등 끄기
        </Button>
      </DialogActions>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          animation: isDanger ? 'controlRoomSirenPulse 1.1s ease-in-out infinite' : 'none',
          '@keyframes controlRoomSirenPulse': {
            '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(198,40,40,0)' },
            '50%': { boxShadow: 'inset 0 0 48px 10px rgba(198,40,40,0.4)' },
          },
        }}
      />
    </Dialog>
  );
};

export default ControlRoomSirenDialog;
